// 585 리본(결혼정보) 랜딩: 긴 이미지 + 상단/하단 버튼 → 8단계 설문 모달 → handleFormSubmit
// 상단·하단 버튼은 동일한 단일 모달/단일 state 를 공유하고, 진행 상태는 localStorage 에 보존한다.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { handleFormSubmit } from "../handlers/formHandlers";
import { validateName, validatePhoneNumber } from "../utils/validation";
import { prohibitedWords } from "../utils/dummyData";
import { validateUrlCode, trackPageClick } from "../utils/api";
import { buildPrivacyNewFormHtml } from "../randing/privacyNewForm";
import MarketingContent from "../randing/MarketingContent";

import "./randing585.scss";

const AD_NUMBER = "585";
const STORAGE_KEY = "randing585_survey";
const IMG_BASE = "https://smilevent-assets.s3.ap-northeast-2.amazonaws.com/images/585";
const FOOTER_TEXT = `Copyright ⓒ 퍼널스\n사업자등록번호 127-88-03197`;
const BG_IMG = `${IMG_BASE}/ribon_v4_q92.jpg`;
const HEADER_IMG = `${IMG_BASE}/header.jpg`;
const TOP_BTN_IMG = `${IMG_BASE}/topButton.png`;
const BOTTOM_BTN_IMG = `${IMG_BASE}/bottomButton.png`;
const FALLBACK_IMG =
  "https://smilevent-assets.s3.ap-northeast-2.amazonaws.com/images/rafinity/rafinity_main.png";

const BIRTH_MIN_YEAR = 1940;
const BIRTH_MIN_AGE = 18;
const TOTAL_STEPS = 8;

// 1~5, 7단계는 선택지, 6단계는 직접 입력
const STEPS = [
  { pre: "", hl: "성별", post: "을 선택해주세요", options: ["남성", "여성"], cols: 2 },
  { pre: "현재 ", hl: "혼인 상태", post: "를 선택해주세요", options: ["초혼", "재혼", "사실혼"], cols: 3 },
  {
    pre: "최종 ",
    hl: "학력",
    post: "을 선택해주세요",
    options: ["대학교 졸업", "석/박사", "대학(2,3년제) 졸업", "고등학교 졸업", "기타"],
    cols: 1,
  },
  {
    pre: "현재 ",
    hl: "직업",
    post: "을 선택해주세요",
    options: [
      "사무/금융직", "연구원/엔지니어", "건축/설계", "교사/강사", "공무원/공사",
      "승무원/항공관련", "서비스/영업", "의사/한의사/약사", "변호사/법조인", "회계사/전문직",
      "간호/의료사", "자영업/사업", "유학생/석박사", "프리랜서/기타",
    ],
    cols: 2,
  },
  {
    pre: "연간 ",
    hl: "소득",
    post: "은 어느 정도인가요?",
    options: [
      "3천만원 미만", "3~4천만원", "4~6천만원", "6~8천만원",
      "8천~1억", "1억~1억5천", "1억5천~2억", "2억 이상",
    ],
    cols: 2,
  },
  { pre: "", hl: "출생년도", post: "를 입력해주세요", input: "birth" },
  {
    pre: "거주 ",
    hl: "지역",
    post: "을 선택해주세요",
    options: [
      "서울", "경기도", "인천", "경상북도(대구)", "경상남도(부산·울산·창원)",
      "충청도(세종·대전)", "전라북도", "전라남도(광주)", "강원도", "제주도",
    ],
    cols: 2,
  },
];

const EMPTY_STATE = { step: 1, answers: Array(7).fill(""), birth: "", name: "", phone: "" };

const loadSaved = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_STATE;
    const p = JSON.parse(raw);
    const answers = Array.isArray(p.answers) ? p.answers.slice(0, 7) : [];
    while (answers.length < 7) answers.push("");
    const step = Number.isInteger(p.step) && p.step >= 1 && p.step <= TOTAL_STEPS ? p.step : 1;
    return {
      step,
      answers: answers.map((a) => (typeof a === "string" ? a : "")),
      birth: typeof p.birth === "string" ? p.birth : "",
      name: typeof p.name === "string" ? p.name : "",
      phone: typeof p.phone === "string" ? p.phone : "",
    };
  } catch (e) {
    return EMPTY_STATE;
  }
};

const clearSaved = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* 저장소 사용 불가 시 무시 */
  }
};

// 추후 실제 신청 건수 조회 API로 교체 대상
const getWeeklyApplicantCount = () => {
  const d = new Date();
  const a = 10000 * Math.sin(31 * d.getDate() + 7 * d.getHours() + 13 * d.getMonth());
  return Math.floor(120 + 50 * (a - Math.floor(a)));
};

function Randing585() {
  const navigate = useNavigate();
  const { urlCode } = useParams();

  // 검증 상태: null 검증 전, true 통과, false 실패
  const [validated, setValidated] = useState(null);
  const [urlCodeSettingId, setUrlCodeSettingId] = useState(null);

  const saved = useMemo(loadSaved, []);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(saved.step);
  const [answers, setAnswers] = useState(saved.answers);
  const [birth, setBirth] = useState(saved.birth);
  const [name, setName] = useState(saved.name);
  const [phone, setPhone] = useState(saved.phone);

  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [viewing, setViewing] = useState(""); // "privacy" | "marketing" | ""
  const [isSubmitting, setIsSubmitting] = useState(false);
  const weeklyCount = useMemo(getWeeklyApplicantCount, []);
  const [submitCount, setSubmitCount] = useState(0);

  // urlCode 검증 (병원번호 없는 /585/:urlCode)
  useEffect(() => {
    // urlCode 없음(/585 미리보기)/"test" 는 검증 생략
    if (!urlCode || urlCode === "test") {
      setUrlCodeSettingId(0);
      setValidated(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await validateUrlCode(urlCode);
        if (cancelled) return;
        const ok = data && data.ad_number === AD_NUMBER;
        setValidated(!!ok);
        if (ok) setUrlCodeSettingId(data.id);
      } catch (error) {
        console.error(error);
        if (!cancelled) setValidated(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [urlCode]);

  // 클릭 카운트는 검증 통과 + 실제 urlCode 일 때만
  useEffect(() => {
    if (validated && urlCodeSettingId) {
      const timer = setTimeout(() => trackPageClick(urlCodeSettingId), 500);
      return () => clearTimeout(timer);
    }
  }, [validated, urlCodeSettingId]);

  // 답변 보존 (동의 체크 상태는 저장하지 않음)
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ step, answers, birth, name, phone })
      );
    } catch (e) {
      /* 저장소 사용 불가 시 무시 */
    }
  }, [step, answers, birth, name, phone]);

  // 모달 오픈 시 배경 스크롤 잠금
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const openModal = () => setOpen(true);
  const closeModal = () => setOpen(false);

  const choose = (idx, value) => {
    setAnswers((prev) => prev.map((a, i) => (i === idx ? value : a)));
    setStep(idx + 2);
  };

  const submitBirth = () => {
    const year = new Date().getFullYear();
    const n = Number(birth);
    if (!/^\d{4}$/.test(birth) || n < BIRTH_MIN_YEAR || n > year - BIRTH_MIN_AGE) {
      alert("출생년도를 정확히 입력해주세요. (예: 1985)");
      return;
    }
    setAnswers((prev) => prev.map((a, i) => (i === 5 ? `${birth}년생` : a)));
    setStep(7);
  };

  const goPrev = () => {
    setViewing("");
    setStep((s) => Math.max(1, s - 1));
  };

  const phoneDigits = phone.replace(/\D/g, "");
  const phoneNumber = {
    first: phoneDigits.slice(0, 3),
    second: phoneDigits.slice(3, 7),
    third: phoneDigits.slice(7, 11),
  };

  const validateInput = () =>
    validateName(name, prohibitedWords) && validatePhoneNumber(phoneNumber);

  const handleSubmit = () => {
    if (!urlCode || urlCode === "test") {
      alert("광고 URL 코드로 접속해야 신청이 접수됩니다. (미리보기 화면)");
      return;
    }
    handleFormSubmit({
      name,
      phoneNumber,
      selectedOption: answers.filter(Boolean).join(" / "),
      isAgreed: agreePrivacy,
      submitCount,
      setSubmitCount,
      validateInput,
      urlCode,
      urlCodeSettingId,
      navigate: (to, opts) => {
        clearSaved();
        navigate(to, opts);
      },
      setIsSubmitting,
    });
  };

  const privacyHtml = useMemo(
    () => buildPrivacyNewFormHtml({ hospital: "리본", trustee: "(주)퍼널스" }),
    []
  );

  if (validated === null) {
    return <div>Loading...</div>;
  }

  if (validated === false) {
    return (
      <>
        <style>{`.rafinity-main-img{display:block;width:80%;height:auto;margin:0 auto}@media (max-width:768px){.rafinity-main-img{width:100%}}`}</style>
        <img className="rafinity-main-img" src={FALLBACK_IMG} alt="main" />
      </>
    );
  }

  const renderTitle = (s) => (
    <h2 className="r585-title">
      {s.pre}
      <em>{s.hl}</em>
      {s.post}
    </h2>
  );

  const renderStepBody = () => {
    if (step >= 1 && step <= 7) {
      const s = STEPS[step - 1];
      const idx = step - 1;
      return (
        <>
          {renderTitle(s)}
          {s.input === "birth" ? (
            <div className="r585-birth">
              <input
                type="tel"
                inputMode="numeric"
                placeholder="예: 1985"
                maxLength={4}
                value={birth}
                onChange={(e) => setBirth(e.target.value.replace(/\D/g, "").slice(0, 4))}
                onKeyDown={(e) => e.key === "Enter" && submitBirth()}
              />
              <button type="button" className="r585-next" onClick={submitBirth}>
                다음
              </button>
            </div>
          ) : (
            <div className={`r585-options cols-${s.cols}`}>
              {s.options.map((opt) => (
                <button
                  type="button"
                  key={opt}
                  className={`r585-opt${answers[idx] === opt ? " selected" : ""}`}
                  onClick={() => choose(idx, opt)}
                >
                  {opt}
                </button>
              ))}
            </div>
          )}
        </>
      );
    }

    // 8단계: 결과 수신 정보
    return (
      <>
        <div className="r585-chips">
          {answers.filter(Boolean).map((a, i) => (
            <span key={i} className="r585-chip">
              {a}
            </span>
          ))}
        </div>
        <h2 className="r585-title">
          진단이 완료되었습니다.
          <br />
          결과를 어디로 보내드릴까요?
        </h2>

        <label className="r585-label" htmlFor="r585-name">
          이름
        </label>
        <input
          id="r585-name"
          className="r585-input"
          type="text"
          placeholder="성함"
          maxLength={20}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <label className="r585-label" htmlFor="r585-phone">
          휴대폰 번호
        </label>
        <input
          id="r585-phone"
          className="r585-input"
          type="tel"
          inputMode="numeric"
          placeholder="010-0000-0000"
          maxLength={11}
          value={phone}
          onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 11))}
        />

        <div className="r585-agrees">
          <div className="r585-agree r585-agree-all">
            <label>
              <input
                type="checkbox"
                checked={agreePrivacy && agreeMarketing}
                onChange={() => {
                  const v = !(agreePrivacy && agreeMarketing);
                  setAgreePrivacy(v);
                  setAgreeMarketing(v);
                }}
              />
              <span>전체 동의</span>
            </label>
          </div>
          {[
            { key: "privacy", label: "상담/예약 서비스 제공 동의 (필수)", checked: agreePrivacy, set: setAgreePrivacy },
            { key: "marketing", label: "마케팅 정보 수신 (선택)", checked: agreeMarketing, set: setAgreeMarketing },
          ].map((a) => (
            <div key={a.key} className="r585-agree">
              <div className="r585-agree-row">
                <label>
                  <input type="checkbox" checked={a.checked} onChange={() => a.set(!a.checked)} />
                  <span>{a.label}</span>
                </label>
                <button
                  type="button"
                  className="r585-view"
                  onClick={() => setViewing(viewing === a.key ? "" : a.key)}
                >
                  [{viewing === a.key ? "닫기" : "자세히보기"}]
                </button>
              </div>
              {viewing === a.key && (
                <div className="r585-agree-body">
                  {a.key === "marketing" ? (
                    <MarketingContent companyName="리본" />
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: privacyHtml }} />
                  )}
                </div>
              )}
            </div>
          ))}
          <p className="r585-benefit">
            ※ 상담/예약 서비스 제공 동의는 필수 항목입니다.
            <br />
            ※ 마케팅 정보 수신은 선택 항목이며, 동의하지 않아도 신청할 수 있습니다.
          </p>
        </div>

        <button type="button" className="r585-submit" onClick={handleSubmit} disabled={isSubmitting}>
          이상형 소개받기
        </button>
      </>
    );
  };

  return (
    <div className="randing-585">
      <header className="r585-header">
        <img className="r585-header-img" src={HEADER_IMG} alt="리본 결혼정보" />
        <button type="button" className="r585-top-btn" onClick={openModal}>
          <img src={TOP_BTN_IMG} alt="이상형 소개받기" />
        </button>
      </header>

      <div className="r585-page">
        {/* 고정 헤더 높이만큼 여백 (1000:137) */}
        <div className="r585-header-spacer" />
        <div className="r585-hero">
          <img className="r585-bg" src={BG_IMG} alt="리본 결혼정보" />
          <div className="r585-counter">
            <span className="r585-dot" />
            이번 주 <b>{weeklyCount}명</b> 신청
          </div>
        </div>

        <footer className="r585-footer">
          {FOOTER_TEXT}
        </footer>
      </div>

      <div className="r585-bottom-bar">
        <button type="button" className="r585-bottom-btn" onClick={openModal}>
          <img src={BOTTOM_BTN_IMG} alt="이상형 소개받기" />
        </button>
      </div>

      {open && (
        <div className="r585-overlay" onClick={closeModal}>
          <div className="r585-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="r585-close" onClick={closeModal} aria-label="닫기">
              ✕
            </button>
            <div className="r585-brand">RE:BORN 매칭 진단</div>
            <div className="r585-progress">
              {Array.from({ length: TOTAL_STEPS }, (_, i) => (
                <span key={i} className={i < step ? "on" : ""} />
              ))}
            </div>
            <div className="r585-stepno">
              STEP {step} / {TOTAL_STEPS}
            </div>

            {renderStepBody()}

            {step > 1 && (
              <button type="button" className="r585-prev" onClick={goPrev}>
                ← 이전
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Randing585;
